import React from "react";
import { authorizedFetch, setSelectedRealm } from "./boot.js";
import { NotificationPreferences } from "./notification_preferences.jsx";
import { Card, Tag } from "./ui.jsx";

async function request(path, options = {}) {
  const response = await authorizedFetch(path, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error?.code || "request_failed");
    error.status = response.status;
    throw error;
  }
  return body;
}

function realmHref(realm) {
  const url = new URL(window.location.href);
  url.searchParams.set("realm", realm);
  url.searchParams.set("tab", "account");
  return url.toString();
}

function roleLabel(membership) {
  if (membership.roleName) return membership.roleName;
  if (membership.role === "owner" || membership.role === "admin") return "Owner";
  if (membership.role === "staff") return "Staff";
  return membership.role || "Member";
}

// Preferences are now only here, so a vendor with a single membership should
// see theirs rather than a closed row that hides the only thing on the list.
// Several memberships keep the disclosure: the whole point of it is that the
// page stays readable when there are six of them.
function MembershipPreferences({ membership, expanded }) {
  const [open, setOpen] = React.useState(expanded);
  return <details className="profile-preference" open={open}
    onToggle={event => setOpen(event.currentTarget.open)}>
    <summary>
      <span><strong>{membership.realmName || membership.realm}</strong><small>Email preferences</small></span>
      <span className="profile-disclosure">{open ? "Hide" : "Show"}</span>
    </summary>
    {open && <NotificationPreferences vendor={membership.realm} requestRealm={membership.realm}/>}
  </details>;
}

export function AccountProfile() {
  const [data, setData] = React.useState(null);
  const [displayName, setDisplayName] = React.useState("");
  const [mobile, setMobile] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [refreshRequired, setRefreshRequired] = React.useState(false);
  const [retry, setRetry] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const mounted = React.useRef(true);
  const busyRef = React.useRef(false);

  const load = React.useCallback(async () => {
    const value = await request("/collections/foundation.profile.get@1/");
    if (!mounted.current) return;
    setData(value);
    setDisplayName(value.displayName || "");
    setMobile(value.mobile || "");
    setRefreshRequired(false);
    setRetry(null);
  }, []);

  React.useEffect(() => {
    mounted.current = true;
    let active = true;
    load().catch(() => {
      if (active) setError("Your profile could not be loaded. Refresh to try again.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; mounted.current = false; };
  }, [load]);

  async function refresh() {
    if (busyRef.current) return;
    setLoading(true); setError(""); setNotice("");
    try { await load(); }
    catch (_) { if (mounted.current) setError("Your profile could not be loaded. Refresh to try again."); }
    finally { if (mounted.current) setLoading(false); }
  }

  async function save(attempt = null) {
    if (busyRef.current || data?.canUpdate !== true) return;
    const name = displayName.trim();
    if (!name) { setError("Enter the name you want shown in the dashboard."); return; }
    const next = attempt || {
      input: { display_name: name, mobile: mobile.trim(), expected_revision: data.revision },
      key: window.crypto.randomUUID(),
    };
    busyRef.current = true; setBusy(true); setError(""); setNotice("Saving your profile…");
    try {
      await request("/actions/foundation.profile.update@1/", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input: next.input, idempotency_key: next.key }),
      });
      if (!mounted.current) return;
      setRefreshRequired(true); setRetry(null);
      try {
        await load();
        if (mounted.current) setNotice("Profile saved.");
      } catch (_) {
        if (mounted.current) setError("Your profile was saved, but the current details could not be loaded. Refresh before editing again.");
      }
    } catch (failure) {
      if (!mounted.current) return;
      setNotice("");
      if ([400, 404, 409, 412].includes(failure.status)) {
        setRetry(null); setRefreshRequired(true);
        setError(failure.status === 409 || failure.status === 412
          ? "Your profile changed somewhere else. Refresh before saving again."
          : "Your profile could not be saved. Refresh and review it before trying again.");
      } else {
        setRetry(next);
        setError("The save could not be confirmed. Retry the same save before making another change.");
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  const locked = loading || busy || refreshRequired || Boolean(retry) || data?.canUpdate !== true;
  const changed = data && (displayName.trim() !== data.displayName || mobile.trim() !== (data.mobile || ""));
  const memberships = data?.memberships || [];

  return <div className="account-profile fade-in">
    <div className="account-profile-grid">
      <Card title="Account details" subtitle="Your details across the Vendor Dashboard">
        {loading && <p role="status">Loading your profile…</p>}
        {error && <p role="alert" className="error-text">{error}</p>}
        {data && <form className="profile-form" onSubmit={event => { event.preventDefault(); save(); }}>
          <label>Display name
            <input type="text" maxLength={200} value={displayName} disabled={locked}
              onChange={event => { setDisplayName(event.target.value); setNotice(""); }}/>
          </label>
          <label>Email
            <input type="email" value={data.email || ""} placeholder="Unavailable" readOnly aria-readonly="true"/>
            <small>{data.email ? "Your sign-in provider manages this address." : "No verified sign-in email is available."}</small>
          </label>
          <label>Mobile
            <input type="tel" maxLength={32} value={mobile} disabled={locked || data.mobileEditable !== true}
              placeholder="Add a mobile number" onChange={event => setMobile(event.target.value)}/>
            <small>Saved to your dashboard account for future SMS features. SMS is not available yet.</small>
          </label>
          <div className="profile-actions">
            {refreshRequired && <button type="button" className="btn" disabled={busy} onClick={refresh}>Refresh profile</button>}
            {retry && <button type="button" className="btn primary" disabled={busy} onClick={() => save(retry)}>Retry same save</button>}
            {!refreshRequired && !retry && <button type="submit" className="btn primary" disabled={locked || !changed}>Save profile</button>}
          </div>
        </form>}
        {!loading && !data && <button type="button" className="btn" onClick={refresh}>Refresh profile</button>}
        {notice && <p className="report-notice" role="status" aria-live="polite">{notice}</p>}
      </Card>

      <Card title="Dashboard access" subtitle="Your vendors, roles and sections">
        {!loading && memberships.length === 0 && <p className="muted">No active dashboard memberships.</p>}
        <div className="profile-memberships">
          {memberships.map(membership => <article className="profile-membership" key={membership.realm}>
            <div className="profile-membership-head">
              <div><strong>{membership.realmName || membership.realm}</strong><span>{roleLabel(membership)}</span></div>
              {membership.isCurrent
                ? <Tag variant="live">Current</Tag>
                : membership.status === "active"
                  ? <a className="btn sm" href={realmHref(membership.realm)}
                      onClick={() => setSelectedRealm(membership.realm)}>Switch</a>
                  : <Tag>{membership.status === "suspended" ? "Suspended" : "Inactive"}</Tag>}
            </div>
            <div className="profile-sections" aria-label={`Sections for ${membership.realmName || membership.realm}`}>
              {(membership.sections || []).length
                ? membership.sections.map(section => <Tag key={section.key || section}>{section.label || section}</Tag>)
                : <span className="caption">No dashboard sections assigned</span>}
            </div>
          </article>)}
        </div>
      </Card>
    </div>

    <div className="profile-preferences-grid">
      {(() => {
        const rows = memberships.filter(row => row.status === "active" && row.preferencesAvailable);
        return rows.map(membership =>
          <MembershipPreferences key={membership.realm} membership={membership} expanded={rows.length === 1}/>);
      })()}
    </div>
  </div>;
}
